import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

const EVM_REGEX = /^0x[0-9a-fA-F]{40}$/;
const BTC_REGEX = /^(bc1[a-z0-9]{39,59}|[13][a-km-zA-HJ-NP-Z1-9]{25,34})$/;

/**
 * Network-aware crypto wallet address validator.
 *
 * Pass the network string at form-control creation time, or call
 * control.setValidators([walletValidator(newNetwork)]) when the network
 * dropdown changes.
 *
 * Supported networks: 'ERC20'/'ERC-20', 'BEP20'/'BEP-20', 'BITCOIN_MAINNET'/'Bitcoin Mainnet'
 * Unknown networks fall back to a minimum-length (26) sanity check only.
 */
export function walletValidator(network: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: unknown = control.value;
    if (!value || typeof value !== 'string' || value.trim() === '') {
      return null; // let Validators.required handle empty
    }

    const address = value.trim();

    // Trim handles leading/trailing whitespace from paste. Catch internal
    // whitespace BEFORE the network-specific regex so the user sees a
    // precise hint ("spaces not allowed") rather than the generic
    // "invalid EVM/Bitcoin address" message.
    if (/\s/.test(address)) {
      return { walletAddress: 'Wallet address must not contain spaces.' };
    }

    switch (network) {
      case 'ERC20':
      case 'ERC-20':
      case 'BEP20':
      case 'BEP-20':
        if (!EVM_REGEX.test(address)) {
          return {
            walletAddress:
              'Invalid EVM address. Must be 0x followed by 40 hex characters.',
          };
        }
        break;

      case 'BITCOIN_MAINNET':
      case 'Bitcoin Mainnet':
        if (!BTC_REGEX.test(address)) {
          return {
            walletAddress:
              'Invalid Bitcoin address. Must be a Legacy (1...), P2SH (3...), or Bech32 (bc1...) address.',
          };
        }
        break;

      default:
        // Unknown network -- minimum length sanity check only
        if (address.length < 26) {
          return { walletAddress: 'Wallet address is too short.' };
        }
    }

    return null;
  };
}

/**
 * General wallet address validator (no network required).
 * Falls through to the default minLength(26) sanity check.
 */
export const generalWalletAddressValidator: ValidatorFn = walletValidator('');
